import React, { FC, useEffect, useRef } from 'react';
import { BodyText, Title } from '@gje/typography/lib';
import Pair from '../../../../types/Pair';
import { StatsDataEnum } from '../../../../types/enums/statsDataEnum';
import {
    resolvePartnerForStats,
    resolveProductNameForStats
} from '../../../../services/stats/stats-service';
import {
    createPurchaseData,
    invokeFbqPurchase,
    invokeUtagData
} from '../../../../utils/statisticsUtils';
import ReactToPrint from 'react-to-print';
import { getText } from '../../../../services/text/text-service';
import { HtmlPanel } from '../../../components/HtmlPanel/HtmlPanel';
import { PrimaryButton } from '@gje/buttons';
import classNames from 'classnames';
import { connect } from 'react-redux';

const lightBulb = require('../../../../images/lightbulb.png');

interface SummaryData {
    sectionsProvider: () => Section[];
    statsData: any;
}

interface StatsData {
    statsData: any;
    productNumber: string;
}

interface StateToProps {
    calculationData: any;
    buyingInformationForm: any;
}

interface Section {
    header: string;
    values: Pair<string, string>[];
}

/**
 * Due to react-to-print requirements, this component need to be class based
 */
class Wrapper extends React.Component<StatsData> {
    interval = setInterval(() => {
        //@ts-ignore
        if (window.utag) {
            invokeUtagData(
                createPurchaseData(
                    StatsDataEnum.APPLICATION_NAME,
                    resolvePartnerForStats(),
                    this.props.statsData.order_id,
                    this.props.statsData.order_total,
                    window.location.pathname +
                        `/${resolveProductNameForStats(
                            this.props.productNumber
                        )}` +
                        StatsDataEnum.SUMMARY,
                    StatsDataEnum.PAGE_TITLE,
                    resolveProductNameForStats(this.props.productNumber),
                    resolveProductNameForStats(this.props.productNumber),
                    '1',
                    this.props.statsData.product_unit_price,
                    resolveProductNameForStats(this.props.productNumber),
                    StatsDataEnum.SUMMARY,
                    '1'
                )
            );
            clearInterval(this.interval);
        }
    }, 500);

    intervalFbq = setInterval(() => {
        if (window.location.host.includes('dev' || 'test')) {
            clearInterval(this.intervalFbq);
        }
        //@ts-ignore
        if (window.fbq) {
            invokeFbqPurchase(
                resolveProductNameForStats(this.props.statsData.productNumber),
                this.props.statsData.order_total
            );
            clearInterval(this.intervalFbq);
        }
    }, 500);

    render() {
        const { children, ...rest } = this.props;
        return (
            <div className="row" {...rest}>
                {this.props.children}
            </div>
        );
    }
}

const CompanyCarSummaryStep: FC<StateToProps & SummaryData> = ({
    sectionsProvider,
    buyingInformationForm,
    calculationData,
    statsData
}) => {
    useEffect(() => window.scrollTo(0, 0), []);
    const toPrintRef = useRef();

    const paymentTermChooser = () => {
        if (buyingInformationForm.values.paymentTerm) {
            return buyingInformationForm.values.paymentTerm;
        } else return calculationData.paymentTerm;
    };

    const isMonthlyPayment = paymentTermChooser() == 12;

    const CommonHeadSection = () => (
        <>
            <Title
                tag="h2"
                className={classNames(
                    'row summary-page-row-padding small-padding-bottom-print',
                    'padding-top-bottom-39'
                )}>
                {getText('webshopsweden.common.summaryPage.header')}
            </Title>
            <BodyText className="row padding-bottom-39 summary-page-row-padding small-padding-bottom-print">
                {getText(
                    'webshopsweden.products.companyCar.summaryPage.description'
                )}
            </BodyText>
            {isMonthlyPayment && (
                <div
                    className={classNames(
                        'row',
                        'summary-page-light-bubble-bg',
                        'display-flex',
                        'align-items',
                        'summary-page-row-padding',
                        'print-bulb'
                    )}>
                    <div>
                        <img
                            id={'lightbulb'}
                            src={lightBulb}
                            alt="lightbulb"
                            width="72px"
                            height="72px"
                        />
                    </div>
                    <BodyText className="col-sm-21">
                        {getText(
                            'webshopsweden.common.summaryPage.instruction'
                        )}
                    </BodyText>
                </div>
            )}
        </>
    );

    const ContentSection = () => (
        <div>
            {sectionsProvider().map(s => (
                <div className={'dont-break-on-print'}>
                    <Title
                        tag="h2"
                        className="row padding-top-bottom-39 summary-page-row-padding small-padding-bottom-top-print">
                        {s.header}
                    </Title>
                    {s.values.map(p => renderUnderlinedField(p.key, p.value))}
                </div>
            ))}
        </div>
    );

    const FooterSection = () => (
        <>
            <div className="row padding-top-bottom-39 summary-page-row-padding small-padding-top-print">
                <BodyText>
                    <div className="col-sm-23">
                        <HtmlPanel>
                            {getText(
                                'webshopsweden.products.companyCar.autogiro'
                            )}
                        </HtmlPanel>
                    </div>
                    <div className="col-sm-23 margin-bottom-39">
                        <HtmlPanel>
                            {getText(
                                'webshopsweden.products.companyCar.autogiroDesc'
                            )}
                        </HtmlPanel>
                    </div>
                    <div className="col-sm-23">
                        <HtmlPanel>
                            {getText(
                                'webshopsweden.common.summaryPage.overview.additionalSalesInfo.companyCar'
                            )}
                        </HtmlPanel>
                    </div>
                </BodyText>
            </div>
            <div className="row padding-top-39 summary-page-row-padding hide-in-print">
                <div className="col-sm-6 margin-bottom-39">
                    {
                        <ReactToPrint
                            trigger={() => (
                                <PrimaryButton
                                    className="summary-button"
                                    borderColor="default"
                                    flexible={true}
                                    asLink={true}>
                                    <Title
                                        tag={'h4'}
                                        children={getText(
                                            'webshopsweden.common.summaryPage.buttons.saveOrPrint'
                                        )}
                                    />
                                </PrimaryButton>
                            )}
                            content={() => toPrintRef.current}
                        />
                    }
                </div>
            </div>
        </>
    );

    return (
        <Wrapper
            statsData={statsData}
            productNumber={calculationData.productNumber}
            ref={toPrintRef}>
            <CommonHeadSection />
            <ContentSection />
            <FooterSection />
        </Wrapper>
    );
};

const renderUnderlinedField = (key: string, value: string) => {
    return (
        <div className="row summary-page-row-padding margin-bottom-26 margin-left-30 display-flex align-items-bottom small-margin-bottom-print">
            <Title
                tag="h5"
                className="col-xs-12 summary-page-underlined-element">
                {key}
            </Title>
            <Title
                tag="h5"
                className="col-xs-11 text-align-right summary-page-underlined-element">
                {value}
            </Title>
        </div>
    );
};

const mapStateToProps = state => ({
    calculationData: state.calculationData.calculatedData.calculation,
    buyingInformationForm: state.form.buyingInformation
});

export default connect<StateToProps>(
    mapStateToProps,
    {}
)(CompanyCarSummaryStep);
